import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Bell,
  ChevronDown,
  ChevronLeft,
  LogOut,
  Moon,
  Search,
  Sparkles,
  Sun,
  UserCircle,
  type LucideIcon,
} from 'lucide-react';
import { useTheme } from '@/lib/theme';
import { useAuthStore } from '@/lib/auth';
import { useFeatureFlags } from '@/lib/feature-flags';
import { applyTenantBranding } from '@/lib/branding';
import { useNavTrail, trailRoot } from '@/lib/nav-context';
import {
  SECTIONS,
  SETTINGS_ITEMS,
  matchDetail,
  type SectionGroup,
} from '@/lib/nav-registry';
import { useCrumbs, useDocumentTitle } from '@/lib/use-crumbs';
import { useLogout } from '@/features/auth/use-logout';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Breadcrumbs } from '@/components/breadcrumbs';
import {
  CommandPalette,
  useCommandPaletteHotkey,
} from '@/components/command-palette';
import { cn } from '@/lib/cn';
import { OnboardingWizard } from '@/features/onboarding/wizard';

/**
 * Sidebar IA — organized around the user's SEO workflow:
 *
 *   Workspace → where you live (Dashboard, Projects)
 *   Research  → understand your site + market (Audits, Keywords, Competitors)
 *   Content   → produce the deliverable (Briefs)
 *   Strategy  → plan, execute, project, measure (Calendars, Campaigns,
 *               Forecasts, Reports) — the closed loop that consumes Research
 *               and Content and turns them into outcomes
 *   Settings  → workspace plumbing (in topbar dropdown, not sidebar)
 */
interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Short plain-English meaning — shown in sidebar tooltips. */
  description?: string;
  /** Treat any pathname starting with this prefix as active. */
  match?: string;
  /** When set, this item renders as a collapsible parent with nested links. */
  children?: NavItem[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Nav groups derived from the shared registry (lib/nav-registry). Sections
 * with `sidebar: false` never render here; feature-flag-gated sections are
 * omitted entirely (rather than rendered-disabled) so they never leak to
 * tenants without access.
 */
const GROUP_LABELS: Record<SectionGroup, string> = {
  workspace: 'Workspace',
  research: 'Research',
  content: 'Content',
  strategy: 'Strategy',
};

function buildNavGroups(flags: Record<string, boolean>): NavGroup[] {
  const groups: NavGroup[] = [];
  for (const group of ['workspace', 'research', 'content', 'strategy'] as const) {
    const items: NavItem[] = SECTIONS.filter(
      (s) =>
        s.group === group &&
        s.sidebar !== false &&
        (!s.featureFlag || flags[s.featureFlag]),
    ).map((s) => ({
      to: s.path,
      label: s.label,
      icon: s.icon,
      ...(s.description ? { description: s.description } : {}),
      ...(s.path === '/' ? { match: '/' } : {}),
    }));
    if (items.length > 0) groups.push({ label: GROUP_LABELS[group], items });
  }
  return groups;
}

/**
 * Settings sub-pages are surfaced from the account dropdown in the topbar
 * rather than the sidebar — they're account/workspace plumbing, not part of
 * the day-to-day operating flow.
 *
 * Account sits at the top because it's the user's own settings; the rest
 * are workspace-scoped admin pages.
 */
const ACCOUNT_LINK = {
  to: SETTINGS_ITEMS[0]!.path,
  label: SETTINGS_ITEMS[0]!.label,
  icon: SETTINGS_ITEMS[0]!.icon,
} as const;

/** Workspace-scoped settings pages (everything except the user's Account). */
const SETTINGS_LINKS: { to: string; label: string; icon: LucideIcon }[] =
  SETTINGS_ITEMS.slice(1).map((s) => ({ to: s.path, label: s.label, icon: s.icon }));

const SIDEBAR_STORAGE_KEY = 'seo:sidebar';

function loadSidebarCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const role = useAuthStore((s) => s.role);
  const logout = useLogout();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const qc = useQueryClient();

  // Are we on a detail page whose entity belongs to a project? If so, the
  // sidebar shows "Projects" as a *context* highlight (secondary) alongside
  // the primary section highlight, so the ancestor relationship stays
  // visible. Detail classification + cache key come from the nav registry.
  const projectContextId = useMemo<string | null>(() => {
    const detail = matchDetail(pathname);
    if (!detail || detail.kind === 'project') return null;
    const entity = qc.getQueryData<{ project_id?: string }>(
      detail.queryKey as unknown[],
    );
    return entity?.project_id ?? null;
  }, [pathname, qc]);

  const [collapsed, setCollapsed] = useState<boolean>(loadSidebarCollapsed);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { theme, toggle: toggleTheme } = useTheme();

  // Registry-driven nav; feature-flagged sections (e.g. Off-Page Brief) are
  // omitted for tenants without the flag.
  const flags = useFeatureFlags();
  const navGroups = useMemo(() => buildNavGroups(flags), [flags]);

  // Journey trail root — the section the user's current journey started
  // from. The trail is recorded centrally by the RootShell router
  // subscription, so EVERY navigation mechanism (sidebar, palette,
  // breadcrumbs, tiles, redirects, Back/Forward) updates it uniformly.
  const trail = useNavTrail((s) => s.trail);
  const root = trailRoot(trail);

  // Breadcrumb trail is shared between the topbar (full), the mobile
  // compact back-crumb, and the document title (N-18).
  const crumbs = useCrumbs();
  useDocumentTitle(crumbs);

  // Move focus to the content region on route change so keyboard and
  // screen-reader users get a navigation signal (skip on initial mount —
  // stealing focus from the first interactive element would be worse).
  const mainRef = useRef<HTMLElement | null>(null);
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    mainRef.current?.focus({ preventScroll: true });
  }, [pathname]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SIDEBAR_STORAGE_KEY,
        collapsed ? 'collapsed' : 'expanded',
      );
    } catch {
      // ignore
    }
  }, [collapsed]);

  useEffect(() => {
    applyTenantBranding(tenant);
  }, [tenant]);

  const togglePalette = useCallback(() => setPaletteOpen((v) => !v), []);
  useCommandPaletteHotkey(togglePalette);

  const isActive = (to: string, match?: string): boolean => {
    // The journey root wins on detail pages: click Dashboard → open an audit
    // tile → sidebar still shows Dashboard, because that's where the journey
    // started. On top-level pages the URL is authoritative — landing on
    // /projects highlights Projects no matter how you got there (the trail
    // resets its root on every section navigation, so these agree).
    if (root) return to === root;
    const m = match ?? to;
    if (m === '/') return pathname === '/';
    return pathname === m || pathname.startsWith(`${m}/`);
  };

  // Soft context indicator: the current detail entity belongs to a project,
  // so "Projects" shows a secondary highlight unless it is already the
  // primary one.
  const isContextActive = (to: string): boolean => {
    if (to !== '/projects') return false;
    if (root === '/projects') return false;
    return !!projectContextId;
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="relative flex min-h-screen text-foreground">
        {/* Keyboard users can jump straight past the sidebar/topbar. */}
        <a
          href="#main-content"
          className="sr-only z-50 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground focus:not-sr-only focus:absolute focus:left-3 focus:top-3"
        >
          Skip to content
        </a>
        <Sidebar
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed((v) => !v)}
          tenantName={tenant?.name ?? 'SEO AI Agent'}
          tenantPlan={tenant?.plan ?? null}
          tenantLogo={tenant?.branding_logo_url ?? null}
          navGroups={navGroups}
          isActive={isActive}
          isContextActive={isContextActive}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <header
            className={cn(
              'sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-border/70 px-4 sm:px-6',
              'bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60',
            )}
          >
            <div className="flex min-w-0 items-center gap-4">
              {/* Full trail on md+, a single back-crumb below md so mobile
                  always has an in-app way up the hierarchy. */}
              <div className="hidden min-w-0 md:block">
                <Breadcrumbs items={crumbs} />
              </div>
              <div className="min-w-0 md:hidden">
                <Breadcrumbs items={crumbs} variant="compact" />
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
              <CommandPaletteTrigger onClick={togglePalette} />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={
                      theme === 'dark'
                        ? 'Switch to light mode'
                        : 'Switch to dark mode'
                    }
                    className="h-9 w-9 rounded-full"
                    onClick={toggleTheme}
                  >
                    {theme === 'dark' ? (
                      <Sun className="h-4 w-4" />
                    ) : (
                      <Moon className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </TooltipContent>
              </Tooltip>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 gap-2 rounded-full"
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
                      {(user?.display_name ?? user?.email ?? 'A')
                        .slice(0, 1)
                        .toUpperCase()}
                    </span>
                    <span className="hidden max-w-[140px] truncate text-sm sm:inline-block">
                      {user?.display_name ?? user?.email ?? 'Account'}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuLabel>
                    <div className="truncate text-sm font-medium text-foreground">
                      {user?.display_name ?? user?.email}
                    </div>
                    <div className="flex items-center gap-2 text-xs font-normal text-muted-foreground">
                      <span className="truncate">{user?.email}</span>
                      {role ? (
                        <span className="shrink-0 rounded-full border border-border/60 bg-muted/50 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider">
                          {role}
                        </span>
                      ) : null}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate({ to: ACCOUNT_LINK.to })}>
                    <UserCircle className="mr-2 h-4 w-4" />
                    {ACCOUNT_LINK.label}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    aria-label="Notifications"
                    onClick={() => {
                      /* Notification center not implemented yet — placeholder. */
                    }}
                  >
                    <span className="relative mr-2 inline-flex h-4 w-4 items-center justify-center">
                      <Bell className="h-4 w-4" />
                      <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
                    </span>
                    Notifications
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Workspace
                  </DropdownMenuLabel>
                  <div className="px-2 pb-1.5 text-xs">
                    <div className="truncate font-medium text-foreground">
                      {tenant?.name ?? 'Workspace'}
                    </div>
                    {tenant?.slug ? (
                      <div className="truncate text-[11px] text-muted-foreground">
                        {tenant.slug}
                        {tenant.plan ? ` · ${tenant.plan}` : ''}
                      </div>
                    ) : null}
                  </div>
                  {SETTINGS_LINKS.map((s) => {
                    const Icon = s.icon;
                    return (
                      <DropdownMenuItem
                        key={s.to}
                        onClick={() => navigate({ to: s.to })}
                      >
                        <Icon className="mr-2 h-4 w-4" />
                        {s.label}
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => logout.mutate()}>
                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main
            id="main-content"
            ref={mainRef}
            tabIndex={-1}
            className="relative flex-1 overflow-auto px-4 py-6 outline-none sm:px-8 sm:py-8"
          >
            <div className="mx-auto w-full max-w-7xl">{children}</div>
          </main>
        </div>

        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />

        {/*
          P0.4 first-time-user onboarding wizard. Renders as a modal overlay
          on every authenticated route until the user completes or explicitly
          dismisses it. Gated internally on `onboarding_state.step !== 'done'`.
        */}
        <OnboardingWizard />
      </div>
    </TooltipProvider>
  );
}

/* ------------------------------------------------------------------ */
/* Sidebar                                                             */
/* ------------------------------------------------------------------ */

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  tenantName: string;
  tenantPlan: string | null;
  tenantLogo: string | null;
  navGroups: NavGroup[];
  isActive: (to: string, match?: string) => boolean;
  isContextActive: (to: string) => boolean;
}

function Sidebar({
  collapsed,
  onToggleCollapsed,
  tenantName,
  tenantPlan,
  tenantLogo,
  navGroups,
  isActive,
  isContextActive,
}: SidebarProps): JSX.Element {
  return (
    <aside
      data-collapsed={collapsed}
      className={cn(
        'sticky top-0 z-40 flex h-screen shrink-0 flex-col border-r border-border/70',
        'bg-card/60 backdrop-blur-xl',
        'transition-[width] duration-300 ease-out',
        collapsed ? 'w-[72px]' : 'w-64',
      )}
    >
      {/* Logo */}
      <div
        className={cn(
          'relative flex h-16 items-center gap-3 border-b border-border/70 px-3',
          collapsed && 'justify-center px-0',
        )}
      >
        <div className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-primary text-primary-foreground shadow-glow-sm">
          {tenantLogo ? (
            <img
              src={tenantLogo}
              alt={tenantName}
              className="h-7 w-7 rounded-md object-contain"
            />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
        </div>
        {!collapsed ? (
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-semibold tracking-tight">
              {tenantName}
            </span>
            {tenantPlan ? (
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {tenantPlan}
              </span>
            ) : null}
          </div>
        ) : null}

        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="absolute -right-3 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-elevation-sm transition-colors hover:text-foreground"
        >
          <ChevronLeft
            className={cn(
              'h-3.5 w-3.5 transition-transform duration-300',
              collapsed && 'rotate-180',
            )}
          />
        </button>
      </div>

      {/* Primary nav */}
      <nav aria-label="Primary" className="flex-1 space-y-5 overflow-y-auto p-2.5">
        {navGroups.map((g) => (
          <SidebarSection
            key={g.label}
            label={g.label}
            collapsed={collapsed}
            items={g.items}
            isActive={isActive}
            isContextActive={isContextActive}
          />
        ))}
      </nav>

      {/* Upgrade CTA */}
      <div className="border-t border-border/70 p-2.5">
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/settings/billing"
                className="flex h-10 w-full items-center justify-center rounded-xl bg-gradient-primary text-xs font-semibold text-primary-foreground shadow-glow-sm"
              >
                <Sparkles className="h-4 w-4" />
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">Upgrade plan</TooltipContent>
          </Tooltip>
        ) : (
          <div className="rounded-xl border border-border/60 bg-gradient-to-br from-primary/8 via-card/50 to-violet-500/8 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Current plan
                </div>
                <div className="truncate text-sm font-semibold">
                  {tenantPlan ?? 'Free'}
                </div>
              </div>
              <Link
                to="/settings/billing"
                className="inline-flex items-center gap-1 rounded-full bg-gradient-primary px-3 py-1 text-[11px] font-semibold text-primary-foreground shadow-glow-sm transition-transform hover:scale-[1.03]"
              >
                <Sparkles className="h-3 w-3" />
                Upgrade
              </Link>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

interface SidebarSectionProps {
  label: string;
  collapsed: boolean;
  items: NavItem[];
  isActive: (to: string, match?: string) => boolean;
  isContextActive: (to: string) => boolean;
}

function SidebarSection({
  label,
  collapsed,
  items,
  isActive,
  isContextActive,
}: SidebarSectionProps): JSX.Element {
  return (
    <div className="space-y-0.5">
      <AnimatePresence initial={false}>
        {!collapsed ? (
          <motion.div
            key={`${label}-heading`}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
          >
            {label}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {items.map((item) => (
        <SidebarItem
          key={item.to}
          item={item}
          collapsed={collapsed}
          isActive={isActive}
          isContextActive={isContextActive}
        />
      ))}
    </div>
  );
}

/**
 * Renders a single nav row. When `item.children` is set, becomes a collapsible
 * parent: in the expanded sidebar, click the row to toggle children; in the
 * icon-only sidebar, click to navigate to the first child (with a tooltip).
 */
function SidebarItem({
  item,
  collapsed,
  isActive,
  isContextActive,
  nested = false,
}: {
  item: NavItem;
  collapsed: boolean;
  isActive: (to: string, match?: string) => boolean;
  isContextActive: (to: string) => boolean;
  nested?: boolean;
}): JSX.Element {
  const Icon = item.icon;
  const hasChildren = !!item.children && item.children.length > 0;
  const childActive = useMemo(
    () => item.children?.some((c) => isActive(c.to, c.match)) ?? false,
    [item.children, isActive],
  );
  const selfActive = isActive(item.to, item.match);
  const active = selfActive || (hasChildren && childActive);
  // Context-active: the URL is a detail page for an entity owned by this
  // section — show a softer indicator so the user still sees the ancestor
  // relationship in the sidebar (e.g. Projects while on `/audits/<id>`).
  const contextActive = !active && isContextActive(item.to);

  // Auto-expand when a child is active. Lift to local state so the user can
  // also toggle manually without losing the auto-expand behavior on navigate.
  const [open, setOpen] = useState<boolean>(childActive);
  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  // ---- Parent with children ----
  if (hasChildren) {
    // Collapsed sidebar: render as a single icon that opens the first child.
    if (collapsed) {
      const first = item.children![0]!;
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={first.to}
              className={cn(
                'group relative flex items-center justify-center rounded-lg px-0 py-2 text-sm font-medium outline-none transition-colors',
                active
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
              )}
            >
              {active ? (
                <span className="absolute left-1 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />
              ) : null}
              <Icon
                className={cn(
                  'relative h-4 w-4 shrink-0',
                  active ? 'text-primary' : '',
                )}
              />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right">{item.label}</TooltipContent>
        </Tooltip>
      );
    }

    // Expanded sidebar: chevron-toggle parent + nested children list.
    return (
      <div className="space-y-0.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={cn(
            'group relative flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm font-medium outline-none transition-colors',
            active
              ? 'text-foreground'
              : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          )}
        >
          <Icon
            className={cn(
              'h-4 w-4 shrink-0 transition-colors',
              active ? 'text-primary' : '',
            )}
          />
          <span className="flex-1 truncate">{item.label}</span>
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>

        <AnimatePresence initial={false}>
          {open ? (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="overflow-hidden"
            >
              <div className="ml-3 mt-0.5 space-y-0.5 border-l border-border/60 pl-2">
                {item.children!.map((child) => (
                  <SidebarItem
                    key={child.to}
                    item={child}
                    collapsed={false}
                    isActive={isActive}
                    isContextActive={isContextActive}
                    nested
                  />
                ))}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }

  // ---- Leaf ----
  // No click handler needed: the RootShell router subscription records every
  // completed navigation into the journey trail, sidebar clicks included.
  const row = (
    <Link
      to={item.to}
      className={cn(
        'group relative flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium outline-none transition-colors',
        active
          ? 'bg-accent text-accent-foreground'
          : contextActive
            ? 'bg-muted/40 text-foreground/90'
            : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        collapsed && 'justify-center px-0',
        nested && !collapsed && 'py-1.5 text-[13px]',
      )}
    >
      {active && !nested ? (
        // Static indicator (no framer-motion layoutId). The shared-layout
        // animation was misposing the bar when routes changed mid-render —
        // measurement raced with the sidebar's scroll/collapse state and the
        // bar landed outside the row's rounded rectangle. Static positioning
        // is inside the row's box and always aligns with the highlight.
        <span
          className={cn(
            'pointer-events-none absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary',
            collapsed && 'left-1',
          )}
          aria-hidden
        />
      ) : contextActive && !nested ? (
        <span
          className={cn(
            'pointer-events-none absolute left-0 top-1/2 h-3 w-[2px] -translate-y-1/2 rounded-r-full bg-primary/40',
            collapsed && 'left-1',
          )}
          aria-hidden
        />
      ) : null}
      <Icon
        className={cn(
          'relative h-4 w-4 shrink-0 transition-colors',
          active
            ? 'text-primary'
            : contextActive
              ? 'text-primary/70'
              : '',
          nested && 'h-3.5 w-3.5',
        )}
      />
      {!collapsed ? (
        <span className="relative truncate">{item.label}</span>
      ) : null}
    </Link>
  );

  const tip = (
    <>
      <div className="font-medium">{item.label}</div>
      {item.description ? (
        <div className="mt-0.5 max-w-[220px] text-xs font-normal text-muted-foreground">
          {item.description}
        </div>
      ) : null}
      {contextActive ? (
        <div className="mt-1 text-[11px] text-muted-foreground">Current context</div>
      ) : null}
    </>
  );

  // Always offer a hover tip when we have a description (or sidebar is icon-only).
  if (collapsed || item.description) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{row}</TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          {tip}
        </TooltipContent>
      </Tooltip>
    );
  }
  return row;
}

/* ------------------------------------------------------------------ */
/* ⌘K trigger                                                          */
/* ------------------------------------------------------------------ */

function CommandPaletteTrigger({
  onClick,
}: {
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group hidden h-9 w-72 items-center gap-2 rounded-lg border border-border/70 bg-card/50 px-2.5 text-sm text-muted-foreground backdrop-blur transition-colors hover:border-border hover:text-foreground md:flex"
    >
      <Search className="h-4 w-4" />
      <span className="flex-1 text-left">Search or jump to…</span>
      <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
        <kbd className="rounded border border-border/70 bg-muted px-1.5 py-0.5 font-mono">
          ⌘
        </kbd>
        <kbd className="rounded border border-border/70 bg-muted px-1.5 py-0.5 font-mono">
          K
        </kbd>
      </span>
    </button>
  );
}
